import { atom, useStore } from 'jotai'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import FileGrantsIpc from '@renderer/ipc/FileGrantsIpc.renderer'
import type { FileGrantSummary } from '@shared/file-grants/file-grant-types'
import { getDeviceUUID } from '@shared/store/impl/device'
import { useTrackAddAttachment } from '../hooks/attachments-hooks'
import type { ChatAttachment, FinalizedAttachment } from '../types'
import { processFile } from '../utils/fileUtils'
import { useAttachmentStore, useRemainingAttachments } from './attachments'
import { useActiveChatId, useGetRealChatId } from './chats'
import { useLocalChatIds } from './local-chat-ids'
import { useAddChatToast } from './toasts'

/**
 * A file or folder on this or another of the user's computers, granted to a chat. The grant itself lives in main's
 * broker, in the sandbox named by the chat's conversation id, and lasts until the app quits.
 */
export type LocalPathAttachment = Extract<FinalizedAttachment, { type: 'localPath' }>

/**
 * `on_other_device`: attached from another computer, so only that one can reattach it.
 * `needs_reattach`: attached from this computer, but its grant is gone (the app restarted since).
 */
export type LocalPathStatus = 'attached' | 'needs_reattach' | 'on_other_device'

const GRANTS_KEY = ['file-grants'] as const

/**
 * A new chat's grant has to exist before its first message is sent, so the composer names the chat itself: the first
 * attach mints this id, the grant goes into the sandbox of that name, and the submit sends it as a client-chosen
 * conversation id (`newConversation`).
 */
const newChatSandboxIdAtom = atom<string | null>(null)

/** Hands the submit flow the id a new chat's attachments were granted under, once. */
export function useTakeNewChatSandboxId(): () => string | undefined {
  const store = useStore()
  return useCallback(() => {
    const sandboxId = store.get(newChatSandboxIdAtom)
    store.set(newChatSandboxIdAtom, null)
    return sandboxId ?? undefined
  }, [store])
}

/**
 * Takes away the chat's access to local paths the composer dropped without sending, as removing their pills does, and
 * forgets a new chat's id so nothing is granted under it again.
 */
export function useDiscardLocalPaths(): (dropped: ReadonlyArray<ChatAttachment>) => void {
  const store = useStore()
  const revokeLocalPath = useRevokeLocalPath()
  return useCallback(
    (dropped) => {
      for (const { attachment, metadata } of dropped) {
        if (attachment?.type === 'localPath') {
          revokeLocalPath(metadata)
        }
      }
      store.set(newChatSandboxIdAtom, null)
    },
    [store, revokeLocalPath],
  )
}

/** The sandbox the composer's attachments go into, or null while a new chat's id is unconfirmed. */
function useResolveComposerSandboxId(): () => string | null {
  const store = useStore()
  const activeChatId = useActiveChatId()
  const getRealChatId = useGetRealChatId()
  const localChatIds = useLocalChatIds()
  return useCallback(() => {
    if (!activeChatId) {
      const sandboxId = store.get(newChatSandboxIdAtom) ?? uuidv4()
      store.set(newChatSandboxIdAtom, sandboxId)
      return sandboxId
    }
    const conversationId = getRealChatId(activeChatId)
    return localChatIds.has(conversationId) ? null : conversationId
  }, [store, activeChatId, getRealChatId, localChatIds])
}

export function useFileAttachSupport() {
  return useQuery({
    queryKey: [...GRANTS_KEY, 'attach-support'],
    queryFn: () => FileGrantsIpc.describeAttachSupport(),
    staleTime: Infinity,
  })
}

function useSandboxGrants(sandboxId: string | undefined) {
  return useQuery({
    queryKey: [...GRANTS_KEY, sandboxId, 'grants'],
    queryFn: () => (sandboxId ? FileGrantsIpc.listGrants({ sandboxId }) : []),
    enabled: !!sandboxId,
  })
}

const CHAT_STARTING_MESSAGE = 'Wait for this chat to finish starting, then try again.'

function describeGrantError(error: unknown, attachment?: LocalPathAttachment): string {
  switch ((error as { code?: string } | undefined)?.code) {
    case 'blocked_path':
      return 'That location is protected, so it can’t be attached.'
    case 'overlapping_grant':
      return 'This chat already has that, or a folder containing it, attached.'
    case 'grant_path_mismatch':
      return `Pick “${attachment?.name ?? 'the original file'}” to reattach it.`
    case 'sandbox_not_found':
      return CHAT_STARTING_MESSAGE
    case 'sandbox_not_enforcing':
    case 'unsupported_platform':
      return 'Attaching files isn’t available on this computer right now.'
    default:
      return 'Something went wrong attaching that. Try again.'
  }
}

/** Adds the pill for a grant the picker just made. */
function useAddGrantAttachment(): (grant: FileGrantSummary, sandboxId: string, deviceId: string) => Promise<void> {
  const { addAttachment } = useAttachmentStore()
  const trackAddAttachment = useTrackAddAttachment()
  return useCallback(
    async (grant, sandboxId, deviceId) => {
      const { deviceName } = await FileGrantsIpc.describeAttachSupport()
      addAttachment({
        attachment: {
          type: 'localPath',
          path: grant.path,
          name: grant.path.split('/').pop() || grant.path,
          kind: grant.kind,
          mode: grant.mode,
          deviceId,
          deviceName,
        },
        metadata: { id: grant.id, sandboxId },
      })
      trackAddAttachment({ type: 'localPath', trigger: 'Attachments Menu' })
    },
    [addAttachment, trackAddAttachment],
  )
}

/** Picks a folder and grants it to the chat, read-only. */
export function useAttachLocalFolder(): () => Promise<void> {
  const resolveSandboxId = useResolveComposerSandboxId()
  const addGrantAttachment = useAddGrantAttachment()
  const addChatToast = useAddChatToast()
  const queryClient = useQueryClient()

  return useCallback(async () => {
    const sandboxId = resolveSandboxId()
    const deviceId = getDeviceUUID()
    if (!sandboxId || !deviceId) {
      addChatToast({ title: 'Can’t attach yet', message: CHAT_STARTING_MESSAGE, type: 'error' })
      return
    }
    try {
      const grant = await FileGrantsIpc.grantPath({ sandboxId, mode: 'read', pick: 'directory' })
      if (grant) {
        await addGrantAttachment(grant, sandboxId, deviceId)
      }
    } catch (error) {
      addChatToast({ title: 'Couldn’t attach', message: describeGrantError(error), type: 'error' })
    } finally {
      void queryClient.invalidateQueries({ queryKey: GRANTS_KEY })
    }
  }, [resolveSandboxId, addGrantAttachment, addChatToast, queryClient])
}

/**
 * Picks files to attach. A file the chat can upload (an image, a PDF, a short text file) is uploaded with the
 * message as before; any other file is granted to the chat, read-only.
 */
export function useAttachLocalFiles(): () => Promise<void> {
  const resolveSandboxId = useResolveComposerSandboxId()
  const addGrantAttachment = useAddGrantAttachment()
  const { addFileAttachment } = useAttachmentStore()
  const remainingAttachments = useRemainingAttachments()
  const trackAddAttachment = useTrackAddAttachment()
  const addChatToast = useAddChatToast()
  const queryClient = useQueryClient()

  return useCallback(async () => {
    const deviceId = getDeviceUUID()
    const sandboxId = (deviceId && resolveSandboxId()) || undefined
    try {
      const picks = await FileGrantsIpc.attachFiles({ sandboxId, limit: remainingAttachments })
      for (const pick of picks) {
        if (pick.type === 'upload') {
          // IPC hands over a copy, so the bytes sit in an ArrayBuffer of their own.
          const bytes = pick.bytes as Uint8Array<ArrayBuffer>
          const { file, value, error } = await processFile(new File([bytes], pick.name, { type: pick.mimeType }))
          if (error) {
            addChatToast({ message: error, type: 'error' })
            continue
          }
          addFileAttachment(file, value)
          trackAddAttachment({ type: 'file', trigger: 'Attachments Menu' })
        } else if (pick.type === 'grant' && sandboxId && deviceId) {
          await addGrantAttachment(pick.grant, sandboxId, deviceId)
        } else if (pick.type === 'failed') {
          addChatToast({
            title: `Couldn’t attach “${pick.name}”`,
            message: describeGrantError({ code: pick.code }),
            type: 'error',
          })
        }
      }
    } catch (error) {
      addChatToast({ title: 'Couldn’t attach', message: describeGrantError(error), type: 'error' })
    } finally {
      void queryClient.invalidateQueries({ queryKey: GRANTS_KEY })
    }
  }, [
    resolveSandboxId,
    addGrantAttachment,
    addFileAttachment,
    remainingAttachments,
    trackAddAttachment,
    addChatToast,
    queryClient,
  ])
}

/** Removing a composer pill must also take the chat's access away. */
export function useRevokeLocalPath(): (metadata: Record<string, unknown> | undefined) => void {
  const queryClient = useQueryClient()
  return useCallback(
    (metadata) => {
      const grantId = metadata?.id
      const sandboxId = metadata?.sandboxId
      if (typeof grantId !== 'string' || typeof sandboxId !== 'string') {
        return
      }
      FileGrantsIpc.revokePath({ sandboxId, grantId })
        .catch((error) => console.warn('Failed to revoke a removed attachment', error))
        .finally(() => void queryClient.invalidateQueries({ queryKey: GRANTS_KEY }))
    },
    [queryClient],
  )
}

/** Where a sent attachment stands on this computer. A chat the backend hasn't confirmed yet counts as attached. */
export function useLocalPathStatus(
  conversationId: string | undefined,
  attachment: LocalPathAttachment,
): LocalPathStatus {
  const getRealChatId = useGetRealChatId()
  const localChatIds = useLocalChatIds()
  const isOnThisDevice = attachment.deviceId === getDeviceUUID()
  const realConversationId = conversationId ? getRealChatId(conversationId) : undefined
  const isConfirmed = !!realConversationId && !localChatIds.has(realConversationId)
  const { data: grants } = useSandboxGrants(isOnThisDevice && isConfirmed ? realConversationId : undefined)

  if (!isOnThisDevice) {
    return 'on_other_device'
  }
  if (!grants) {
    return 'attached'
  }
  return grants.some((grant) => grant.path === attachment.path) ? 'attached' : 'needs_reattach'
}

export function useReattachLocalPath(): (conversationId: string, attachment: LocalPathAttachment) => Promise<void> {
  const getRealChatId = useGetRealChatId()
  const addChatToast = useAddChatToast()
  const queryClient = useQueryClient()
  return useCallback(
    async (conversationId, attachment) => {
      try {
        await FileGrantsIpc.grantPath({
          sandboxId: getRealChatId(conversationId),
          mode: attachment.mode,
          reattachPath: attachment.path,
        })
      } catch (error) {
        addChatToast({ title: 'Couldn’t reattach', message: describeGrantError(error, attachment), type: 'error' })
      } finally {
        void queryClient.invalidateQueries({ queryKey: GRANTS_KEY })
      }
    },
    [getRealChatId, addChatToast, queryClient],
  )
}
